import { Cable, Wifi } from 'lucide-react';
import { Link } from 'react-router';
import type { EndpointLink } from '../hooks/useEndpoints';
import { ClientTrafficSection } from './traffic/ClientTrafficSection';
import { LinkTrafficSection } from './traffic/LinkTrafficSection';
import type { TrafficRange } from './traffic/range';

/**
 * One line of an endpoint's "Connected to": where it is attached, on what, and through whom when it
 * is only seen behind another device. `linkDevice` makes the device name a link to its page, for
 * surfaces where the line is not itself a button.
 */
export function EndpointLinkSummary({ link, linkDevice = false }: { link: EndpointLink; linkDevice?: boolean }) {
  const deviceName = link.deviceName ?? link.deviceId.slice(0, 8);
  return (
    <span className="inline-flex items-center gap-2 flex-wrap">
      {link.kind === 'wireless' ? <Wifi className="h-3.5 w-3.5" /> : <Cable className="h-3.5 w-3.5" />}
      {linkDevice ? (
        <Link to={`/devices/${link.deviceId}`} className="hover:underline">
          {deviceName}
        </Link>
      ) : (
        <span>{deviceName}</span>
      )}
      {link.interfaceName ? <span className="font-mono text-xs text-muted-foreground">:{link.interfaceName}</span> : null}
      {link.ssid ? <span className="text-xs text-muted-foreground">{link.ssid}</span> : null}
      {link.vlanId !== null ? <span className="text-xs text-muted-foreground">VLAN {String(link.vlanId)}</span> : null}
      {link.behindDeviceName ? <span className="text-xs text-muted-foreground">behind {link.behindDeviceName}</span> : null}
    </span>
  );
}

/**
 * The attached side's traffic for one of an endpoint's links: the switch port's for a cable, the
 * access point's count of the client for wireless. A link seen only behind another device has none:
 * that port carries the neighbour's traffic, not this endpoint's.
 */
export function EndpointLinkTraffic({
  endpointId,
  link,
  range,
  onRangeChange,
}: {
  endpointId: string;
  link: EndpointLink;
  range?: TrafficRange;
  onRangeChange?: (range: TrafficRange) => void;
}) {
  if (link.behindDeviceName) return null;
  return link.kind === 'wired' ? (
    <LinkTrafficSection connectionId={link.linkId} range={range} onRangeChange={onRangeChange} />
  ) : (
    <div className="mt-3 bg-card rounded-md">
      <ClientTrafficSection endpointId={endpointId} range={range} onRangeChange={onRangeChange} />
    </div>
  );
}
